// Firebase-Anbindung für den Games-Bereich.
// Ein Leaderboard-Eintrag pro Spiel & Nutzer = persönliche Bestleistung.
import { useState, useEffect } from "react";
import { db } from "../firebase";
import { postToBackend } from "../credits";
import {
  getAuth,
  signInWithPopup,
  signOut,
  GoogleAuthProvider,
  onAuthStateChanged,
} from "firebase/auth";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  getCountFromServer,
  serverTimestamp,
} from "firebase/firestore";
import { GAMES } from "./registry";
import { ensureUserDocument } from "../credits";

export function useGameAuth() {
  const auth = getAuth();
  const [user, setUser] = useState(auth.currentUser);
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthReady(true);
    });
    return () => unsub();
  }, [auth]);

  const login = async () => {
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      const loggedInUser = result.user;
      // Gleicher Weg wie beim Login auf der Schriftbot-Seite: Wer sich hier
      // zuerst anmeldet, ist genauso ein neues Konto und bekommt dieselben
      // Gratis-Downloads. Vorher wurde hier ein zweites Mal ein Dokument mit
      // credits: 0 angelegt — je nachdem, wo jemand einstieg, gab es die
      // Gutschrift oder eben nicht.
      await ensureUserDocument(loggedInUser);
      return loggedInUser;
    } catch (error) {
      console.error("Login Fehler:", error);
      return null;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Logout Fehler:", error);
    }
  };

  return { user, authReady, login, logout };
}

// --- Games-Username -------------------------------------------------------
// Spieler treten in den Ranglisten mit einem selbst gewählten Username auf,
// nie mit ihrem Google-Namen. Gespeichert im users-Dokument.

export const USERNAME_MIN = 3;
export const USERNAME_MAX = 16;

export function validateUsername(name) {
  const trimmed = (name || "").trim();
  if (trimmed.length < USERNAME_MIN)
    return `Mindestens ${USERNAME_MIN} Zeichen.`;
  if (trimmed.length > USERNAME_MAX)
    return `Höchstens ${USERNAME_MAX} Zeichen.`;
  if (!/^[a-zA-Z0-9ÄÖÜäöüß_.\- ]+$/.test(trimmed))
    return "Nur Buchstaben, Zahlen, Punkt, _ und - erlaubt.";
  return null;
}

// Lädt den Games-Username des eingeloggten Nutzers und erlaubt, ihn zu
// setzen/ändern. Beim Ändern werden bestehende Ranglisten-Einträge umbenannt.
export function useGameProfile(user) {
  const [username, setUsername] = useState(null);
  const [profileReady, setProfileReady] = useState(false);

  useEffect(() => {
    let active = true;
    if (!user) {
      setUsername(null);
      setProfileReady(true);
      return;
    }
    setProfileReady(false);
    getDoc(doc(db, "users", user.uid))
      .then((snap) => {
        if (!active) return;
        setUsername(snap.exists() ? snap.data().gamesUsername || null : null);
        setProfileReady(true);
      })
      .catch((error) => {
        console.error("Profil laden fehlgeschlagen:", error);
        if (active) setProfileReady(true);
      });
    return () => {
      active = false;
    };
  }, [user]);

  const saveUsername = async (rawName) => {
    const current = getAuth().currentUser;
    const name = (rawName || "").trim();
    const invalid = validateUsername(name);
    if (invalid || !current) return { ok: false, error: invalid || "Nicht angemeldet." };
    try {
      await setDoc(
        doc(db, "users", current.uid),
        { gamesUsername: name },
        { merge: true }
      );
      // Bestehende Ranglisten-Einträge auf den neuen Namen umschreiben
      await Promise.all(
        GAMES.map(async (g) => {
          const ref = entryRef(g.id, current.uid);
          const snap = await getDoc(ref);
          if (snap.exists()) await updateDoc(ref, { name });
        })
      );
      setUsername(name);
      return { ok: true };
    } catch (error) {
      console.error("Username speichern fehlgeschlagen:", error);
      return { ok: false, error: "Speichern fehlgeschlagen. Versuch es nochmal." };
    }
  };

  return { username, profileReady, saveUsername };
}

// --- Münzen ---------------------------------------------------------------
// In den Spielen eingesammelte Münzen landen als gameCoins im users-Dokument.
// Münzen werden nur gestapelt — die Umwandlung in Download-Credits passiert
// ausschließlich über redeemCoins() (Button "Münzen einlösen" bei Schriftbot).

export const COINS_PER_PDF = 10;

// Aktueller Münzstand des eingeloggten Nutzers.
export async function fetchMyCoins() {
  const user = getAuth().currentUser;
  if (!user) return 0;
  try {
    const snap = await getDoc(doc(db, "users", user.uid));
    return snap.exists() ? snap.data().gameCoins || 0 : 0;
  } catch (error) {
    console.error("Münzen laden fehlgeschlagen:", error);
    return 0;
  }
}

// Schreibt eingesammelte Münzen gut (nur stapeln, keine Umwandlung).
// Läuft über das Backend: Münzen werden zu Download-Credits, deshalb darf der
// Browser gameCoins nicht selbst schreiben. Der Server begrenzt pro Runde und
// pro Tag (siehe /award-coins).
// Gibt { coins } zurück (coins = neuer Gesamtstand).
export async function awardCoins(collected) {
  const amount = Math.max(0, Math.min(50, Math.floor(collected || 0)));
  if (!getAuth().currentUser || amount === 0) return null;
  const result = await postToBackend("/award-coins", { coins: amount });
  return result ? { coins: result.coins } : null;
}

// Löst gesammelte Münzen ein: volle 10er-Pakete werden in Download-Credits
// umgewandelt, der Rest bleibt als Münzstand stehen. Umgerechnet wird auf dem
// Server gegen den gespeicherten Münzstand.
// Gibt { redeemedCredits, coins } zurück (coins = Reststand).
export async function redeemCoins() {
  if (!getAuth().currentUser) return null;
  return postToBackend("/redeem-coins");
}

const entryRef = (gameId, uid) =>
  doc(db, "leaderboards", gameId, "entries", uid);

// Speichert den Score nur, wenn er die bisherige Bestleistung übertrifft.
// Gibt { best, isNewBest } zurück.
export async function submitScore(gameId, score, name) {
  const user = getAuth().currentUser;
  const safeScore = Math.max(0, Math.min(999999, Math.floor(score)));
  if (!user) return { best: safeScore, isNewBest: false, savedOffline: true };

  const ref = entryRef(gameId, user.uid);
  try {
    const snap = await getDoc(ref);
    const prevBest = snap.exists() ? snap.data().score || 0 : -1;
    if (safeScore > prevBest) {
      await setDoc(ref, {
        uid: user.uid,
        name: name || "Anonym",
        score: safeScore,
        updatedAt: serverTimestamp(),
      });
      return { best: safeScore, isNewBest: true };
    }
    return { best: prevBest, isNewBest: false };
  } catch (error) {
    console.error("Score speichern fehlgeschlagen:", error);
    return { best: safeScore, isNewBest: false, error: true };
  }
}

// Platz in der Weltrangliste für eine gegebene Bestleistung.
export async function fetchMyRank(gameId, score) {
  try {
    const q = query(
      collection(db, "leaderboards", gameId, "entries"),
      where("score", ">", score)
    );
    const snap = await getCountFromServer(q);
    return snap.data().count + 1;
  } catch (error) {
    console.error("Rang laden fehlgeschlagen:", error);
    return null;
  }
}

export async function fetchMyBest(gameId) {
  const user = getAuth().currentUser;
  if (!user) return null;
  try {
    const snap = await getDoc(entryRef(gameId, user.uid));
    return snap.exists() ? snap.data().score || 0 : null;
  } catch (error) {
    console.error("Bestleistung laden fehlgeschlagen:", error);
    return null;
  }
}

// Lädt die Top-Scores; bei Netzwerk-/Firestore-Fehlern wird bis zu zweimal
// erneut versucht. Gibt null zurück, wenn alle Versuche scheitern — der
// Aufrufer kann dann seine bisherige Liste behalten statt "leer" anzuzeigen.
export async function fetchTopScores(gameId, n = 10) {
  const q = query(
    collection(db, "leaderboards", gameId, "entries"),
    orderBy("score", "desc"),
    limit(n)
  );
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const snap = await getDocs(q);
      return snap.docs.map((d, i) => ({ rank: i + 1, ...d.data() }));
    } catch (error) {
      console.error("Leaderboard laden fehlgeschlagen:", error);
      if (attempt < 2)
        await new Promise((r) => setTimeout(r, 500 * (attempt + 1)));
    }
  }
  return null;
}
